/**
 * Rolling-window (1h/5h/24h/7d/30d/lifetime) tool-call-batching efficiency panel — "is
 * the batching-nudge hook actually working" (2026-08-14 follow-up to
 * batching-nudge.py: "make the number itself visible and recurring... the same
 * forcing function that worked for the Claude billing multiplier could apply here").
 *
 * A SEPARATE panel rather than new columns on TaskUsageWindowsPanel, for two reasons:
 * (1) that table's columns are asserted by position (nth()) in several Playwright
 * specs — inserting columns would break them; (2) this metric's scope is genuinely
 * different — every assistant turn fleet-wide, not just turns belonging to a
 * COMPLETED backlog task (TaskUsageWindowsPanel explicitly excludes in-progress work).
 *
 * TWO DIFFERENT TARGET SHAPES — do not conflate them (operator correction 2026-08-14,
 * after an initial "50% relative improvement" framing was wrong for one of the two):
 *   - multi_tool_turn_pct: target is 50% ABSOLUTE (~10x the 4.3% laptop baseline
 *     measured before the hook's threshold lowered), not 50% relative-to-baseline.
 *   - avg_commands_per_bash_call: target IS relative +50% (2.50 -> 3.75).
 * The progress-toward-target renderer below applies each convention to the right
 * field; getting this backwards would silently misreport progress on one of the two.
 */

import { useEffect, useState } from "react";

import { api } from "./api";
import { Panel, fmtTokenCount } from "./components";
import {
  PROVIDER_MODEL_FILTER_OPTIONS,
  ROLE_GROUP_FILTER_OPTIONS,
  accountFilterOptions,
  fmtRatioOrDash,
  fmtTokenAvgOrDash,
  sortWindows,
  windowLabel,
} from "./TaskUsageWindows";
import type { AccountView, BatchingStatsWindowView } from "./types";
import { useSharedProviderFilterIndex } from "./UsageFilterStore";

const POLL_INTERVAL_MS = 60000;

// Operator-set targets (2026-08-14) — see module docstring for why these use two
// different conventions. Kept as named constants (not inlined) so a future re-target
// is a one-line change, not a re-derivation.
const MULTI_TOOL_TURN_PCT_TARGET_ABSOLUTE = 50.0;
const AVG_COMMANDS_PER_BASH_CALL_TARGET_RELATIVE_GROWTH = 0.5; // 2.50 * 1.5 -> 3.75

/** "34.2%" — "—" for null (no turns in this window). */
export function fmtPctOrDash(n: number | null): string {
  if (n === null) return "—";
  return `${n.toFixed(1)}%`;
}

/** Progress toward the multi_tool_turn_pct target, expressed as "how far along the
 * ABSOLUTE 0->50% path are we" — e.g. 5.7% reads as "11% of the way to 50%", not
 * "already close" the way a relative framing would wrongly suggest. Null propagates
 * (nothing to report without a real measurement). */
export function multiToolTurnProgressPct(current: number | null): number | null {
  if (current === null) return null;
  return Math.min(100, Math.max(0, (current / MULTI_TOOL_TURN_PCT_TARGET_ABSOLUTE) * 100));
}

/** Progress toward the avg_commands_per_bash_call target, expressed as "how far along
 * the RELATIVE +50% growth path are we" — e.g. going from 2.50 to 2.65 is 10% of the
 * way to the 3.75 target, regardless of the absolute starting value. A caller must
 * supply the baseline it's measuring growth from; there is no fleet-wide single
 * baseline stored server-side (this metric is a live snapshot, not a stored
 * before/after pair like the CLI probe script's explicit-window comparison). */
export function avgCommandsPerBashCallProgressPct(current: number | null, baseline: number): number | null {
  if (current === null || baseline <= 0) return null;
  const target = baseline * (1 + AVG_COMMANDS_PER_BASH_CALL_TARGET_RELATIVE_GROWTH);
  const span = target - baseline;
  if (span <= 0) return null;
  return Math.min(100, Math.max(0, ((current - baseline) / span) * 100));
}

export function BatchingEfficiencyPanel({ baseUrl }: { baseUrl: string }) {
  const [windows, setWindows] = useState<BatchingStatsWindowView[]>([]);
  const [filterIndex, setFilterIndex] = useSharedProviderFilterIndex();
  const [roleGroupIndex, setRoleGroupIndex] = useState(0);
  const [accounts, setAccounts] = useState<AccountView[]>([]);
  const [accountIndex, setAccountIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const filter = PROVIDER_MODEL_FILTER_OPTIONS[filterIndex] ?? PROVIDER_MODEL_FILTER_OPTIONS[0];
  const roleGroupFilter = ROLE_GROUP_FILTER_OPTIONS[roleGroupIndex] ?? ROLE_GROUP_FILTER_OPTIONS[0];
  // Scoped to the shared provider filter — see TaskUsageWindowsPanel's identical
  // comment (2026-08-19 operator report).
  const accountOptions = accountFilterOptions(accounts, filter.provider);
  const accountFilter = accountOptions[accountIndex] ?? accountOptions[0];
  // cost_ranked_model_routing_rebuild_2026_08_28: restore model dimension to
  // the fleet-wide batching breakdown — token/batching numbers only, never a
  // per-model multiplier (see TaskUsageWindowsPanel's identical note).
  const [modelBreakdown, setModelBreakdown] = useState<{ label: string; windows: BatchingStatsWindowView[] }[]>([]);
  const [modelBreakdownError, setModelBreakdownError] = useState<string | null>(null);

  // Same "degrade to All accounts on failure, never blank the panel" contract as
  // TaskUsageWindowsPanel's own account-registry fetch.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.listAccounts(baseUrl);
        if (!cancelled) setAccounts(data);
      } catch {
        if (!cancelled) setAccounts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  // The previously selected account may not belong to the newly selected provider —
  // reset to "All accounts" (see TaskUsageWindowsPanel's identical reset).
  useEffect(() => {
    setAccountIndex(0);
  }, [filter.provider]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.getBatchingStatsWindows(
          baseUrl,
          filter.provider,
          filter.model,
          roleGroupFilter.roleGroup,
          accountFilter?.accountId,
        );
        if (!cancelled) {
          setWindows(data);
          setError(null);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, filter.provider, filter.model, roleGroupFilter.roleGroup, accountFilter?.accountId]);

  // cost_ranked_model_routing_rebuild_2026_08_28: restore model dimension for
  // batching-efficiency breakdowns — same additive-window pattern as
  // TaskUsageWindowsPanel, per-model batching stats re-queried live.
  useEffect(() => {
    let cancelled = false;
    const loadBreakdown = async () => {
      let slices: { provider: string | undefined; model: string | undefined; label: string }[] = [];
      if (filter.provider === undefined) {
        slices = PROVIDER_MODEL_FILTER_OPTIONS.filter((o) => o.provider !== undefined && o.model === undefined).map(
          (o) => ({ provider: o.provider, model: undefined, label: o.label }),
        );
      } else if (filter.model === undefined) {
        const modelSlices = PROVIDER_MODEL_FILTER_OPTIONS.filter(
          (o) => o.provider === filter.provider && o.model !== undefined,
        );
        if (modelSlices.length > 0)
          slices = modelSlices.map((o) => ({ provider: o.provider, model: o.model, label: o.label }));
        else {
          if (!cancelled) {
            setModelBreakdown([]);
            setModelBreakdownError(null);
          }
          return;
        }
      } else {
        if (!cancelled) {
          setModelBreakdown([]);
          setModelBreakdownError(null);
        }
        return;
      }
      try {
        const results = await Promise.all(
          slices.map(async (s) => {
            const ws = await api.getBatchingStatsWindows(
              baseUrl,
              s.provider,
              s.model,
              roleGroupFilter.roleGroup,
              accountFilter?.accountId,
            );
            return { label: s.label, windows: ws };
          }),
        );
        if (!cancelled) {
          setModelBreakdown(results);
          setModelBreakdownError(null);
        }
      } catch (e) {
        if (!cancelled) setModelBreakdownError(e instanceof Error ? e.message : String(e));
      }
    };
    void loadBreakdown();
    const id = setInterval(() => void loadBreakdown(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, filter.provider, filter.model, roleGroupFilter.roleGroup, accountFilter?.accountId]);

  const sorted = sortWindows(windows);

  return (
    <Panel title="Batching Efficiency" tone="accounts">
      <div className="usage-panel-hint">
        Fleet-wide, every session — is the batching-nudge/block hooks actually reducing unbatched sequential tool calls.
        Queried live over the poller's precomputed per-turn ledger (the expensive transcript scan still only runs on the
        poller's own cadence).
      </div>
      <div className="task-usage-provider-toggle">
        {PROVIDER_MODEL_FILTER_OPTIONS.map((opt, i) => (
          <button
            key={opt.label}
            className={`btn btn-xs ${filterIndex === i ? "active" : ""}`}
            onClick={() => setFilterIndex(i)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="task-usage-role-group-toggle">
        {ROLE_GROUP_FILTER_OPTIONS.map((opt, i) => (
          <button
            key={opt.label}
            className={`btn btn-xs ${roleGroupIndex === i ? "active" : ""}`}
            onClick={() => setRoleGroupIndex(i)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {accountOptions.length > 1 && (
        <div className="task-usage-account-toggle">
          {accountOptions.map((opt, i) => (
            <button
              key={opt.label}
              className={`btn btn-xs ${accountIndex === i ? "active" : ""}`}
              onClick={() => setAccountIndex(i)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {!error && !loaded && <div className="task-usage-pending">loading…</div>}
      {!error && loaded && sorted.length === 0 && (
        <div className="task-usage-pending">no turns match this filter yet</div>
      )}
      {!error && sorted.length > 0 && (
        <table className="task-usage-window-table">
          <thead>
            <tr>
              <th></th>
              <th>Turns</th>
              <th title="Turns with 2+ tool calls bundled — batching WORKING. Target: 50% ABSOLUTE, not relative.">
                Multi-tool turn %
              </th>
              <th>Single-tool turns</th>
              <th title="Total tool calls across every turn in this window">Tool calls</th>
              <th>Output tok/turn</th>
              <th>Bash calls</th>
              <th title="Commands chained via &&/; per Bash call. Target: relative +50% (2.50 -> 3.75).">
                Commands/Bash-call
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((w) => (
              <tr key={w.window}>
                <td className="task-usage-window-label">{windowLabel(w.window)}</td>
                <td className="mono">{w.turns}</td>
                <td className="mono">{fmtPctOrDash(w.multi_tool_turn_pct)}</td>
                <td className="mono">{w.single_tool_turns}</td>
                <td className="mono">{fmtTokenCount(w.tool_calls)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.output_tokens_per_turn)}</td>
                <td className="mono">{fmtTokenCount(w.bash_calls)}</td>
                <td className="mono">{fmtRatioOrDash(w.avg_commands_per_bash_call)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* cost_ranked_model_routing_rebuild_2026_08_28: model dimension restore —
          batching-efficiency breakdown by model/provider (30d), additive to the
          aggregate table above. No multiplier logic here. */}
      {!error && loaded && modelBreakdown.length > 0 && (
        <div data-testid="batching-efficiency-model-breakdown">
          <div className="usage-panel-hint">
            Breakdown by {filter.provider === undefined ? "provider" : "model"} (30d batching) — multipliers stay per
            (provider, plan, date-window), not per-model.
          </div>
          {modelBreakdownError && (
            <div className="task-usage-error">Failed to load model breakdown: {modelBreakdownError}</div>
          )}
          <table className="task-usage-window-table task-usage-model-breakdown-table">
            <thead>
              <tr>
                <th>{filter.provider === undefined ? "Provider" : "Model"}</th>
                <th>Turns</th>
                <th>Multi-tool turn %</th>
                <th>Tool calls</th>
                <th>Out tok/turn</th>
              </tr>
            </thead>
            <tbody>
              {modelBreakdown.map((slice) => {
                const w30 =
                  [...slice.windows].find((w) => w.window === "30d") ??
                  [...slice.windows].find((w) => w.window === "lifetime");
                if (!w30) return null;
                return (
                  <tr key={slice.label}>
                    <td className="task-usage-window-label">{slice.label}</td>
                    <td className="mono">{w30.turns}</td>
                    <td className="mono">{fmtPctOrDash(w30.multi_tool_turn_pct)}</td>
                    <td className="mono">{fmtTokenCount(w30.tool_calls)}</td>
                    <td className="mono">{fmtTokenAvgOrDash(w30.output_tokens_per_turn)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
